import { useQuery } from '@tanstack/react-query';

import { listWorkspaceFiles } from '@/lib/api/generated/api';
import { collectFolderFilePaths } from '../folders';
import { fileKeys } from '../queryKeys';

/**
 * How many files a folder holds, at any depth.
 *
 * Read before a folder is deleted, so the reader is told how much they are
 * agreeing to remove before they agree to it. Not read *by* the delete: the
 * service removes the whole prefix in one call and never sees this list.
 *
 * Calls the listing endpoint directly rather than going through
 * `useWorkspaceFileListing` — this is a walk over paths not known until the
 * level above has been read, and a hook cannot be called per level. What it
 * counts still matches what the browser shows: `collectFolderFilePaths` leaves
 * out the placeholder holding an empty folder open, so a folder made and never
 * filled reports no files rather than one nobody put there.
 */
export function useFolderContents(
  workspaceId: string,
  folderPath: string | undefined,
) {
  const query = useQuery({
    queryKey: fileKeys.folderContents(workspaceId, folderPath ?? ''),
    queryFn: () =>
      collectFolderFilePaths(async (path) => {
        const response = await listWorkspaceFiles(workspaceId, { path });
        if (response.status !== 200) {
          throw new Error(`Could not read “${path}”.`);
        }
        return response.data.entries;
      }, folderPath ?? ''),
    enabled: folderPath !== undefined,
    retry: false,
    // A folder's contents are read to be deleted; a stale answer would delete
    // the wrong number of things.
    gcTime: 0,
    staleTime: 0,
  });

  return {
    filePaths: query.data,
    isCounting: query.isPending && folderPath !== undefined,
    countFailed: query.isError,
  };
}
